import { useDebounce } from '@bob/library'
import { trackAddToCart, trackRemoveFromCart } from '@websrc/App/Helpers/tracking'
import { BasketItem } from '@websrc/Typings/DanteBasket'
import { useEffect, useRef } from 'react'

import { useBasketApi } from './useBasketApi'
import { useBasketState } from './useBasketState'
import { useCoralProductMeta, useProductMeta } from './useProductMeta'

export const useBasketItemQuantity = (item: BasketItem, quantity: number) => {
  const bouncedQuantity = useDebounce(quantity, 300)
  const basketApi = useBasketApi()
  const { quantity: serverQuantity, productNumber, totalPrice, discountPrice, unitPrice } = item
  const productMeta = useProductMeta(item.productNumber)
  const coralProductMeta = useCoralProductMeta(item.productNumber)
  const resolvedProductMeta = productMeta.product_number ? productMeta : coralProductMeta
  const basketState = useBasketState()

  const prevServerQuantity = useRef(serverQuantity)
  const serverQuantityChanged = prevServerQuantity.current !== serverQuantity
  prevServerQuantity.current = serverQuantity

  useEffect(() => {
    // If the frontend quantity changed, we call the API to update it server-side
    // If the server-side quantity changed, we shouldn't call the API as it will revert the change
    if (serverQuantityChanged) {
      return
    }

    // bouncedQuantity must be up to date, and it should differ from the serverQuantity
    // otherwise we risk reverting quantity changes made outside this hook
    if (bouncedQuantity === quantity && bouncedQuantity !== serverQuantity) {
      const trackingQuantity = Math.abs(bouncedQuantity - serverQuantity)

      if (bouncedQuantity < serverQuantity) {
        trackRemoveFromCart(
          'cart removed from cart',
          resolvedProductMeta,
          bouncedQuantity,
          trackingQuantity,
          discountPrice ?? unitPrice,
          basketState
        )
      } else {
        trackAddToCart(
          'cart add to cart',
          resolvedProductMeta,
          trackingQuantity,
          discountPrice ?? unitPrice,
          basketState
        )
      }

      void basketApi(`/items/set/${productNumber}/${bouncedQuantity}`, 'POST')
    }
  }, [
    basketApi,
    productNumber,
    bouncedQuantity,
    serverQuantity,
    totalPrice,
    unitPrice,
    discountPrice,
    basketState,
    serverQuantityChanged,
    quantity,
    productMeta,
    coralProductMeta,
    resolvedProductMeta,
  ])
}
